/**
 * Authentication Context Provider using MSAL.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { InteractionStatus, AccountInfo, SilentRequest } from '@azure/msal-browser';
import type { AuthContextValue, AuthenticatedUser, TokenClaims } from '../types';
import { loginRequest, tokenRequest } from './msalConfig';

/** Include openid+profile so acquireTokenSilent refreshes the ID token (with roles), not just the access token */
const idTokenRefreshRequest = {
  ...tokenRequest,
  scopes: ['openid', 'profile', ...tokenRequest.scopes],
};

/**
 * Auth context with default values.
 */
const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
  accessToken: null,
  login: async () => {},
  loginRedirect: async () => {},
  logout: async () => {},
  logoutRedirect: async () => {},
  getAccessToken: async () => null,
  clearError: () => {},
  forceLogout: () => {},
});

/**
 * Parse token claims from account info, with optional override claims from a fresh token response.
 */
const parseUserFromAccount = (account: AccountInfo | null, overrideClaims?: TokenClaims): AuthenticatedUser | null => {
  if (!account) return null;

  const claims = overrideClaims ?? (account.idTokenClaims as TokenClaims | undefined);

  // Parse roles: Entra ID uses 'roles' array claim, B2C uses 'extension_Roles' string
  const entraRoles = claims?.roles ?? [];
  const b2cRolesStr = claims?.extension_Roles ?? '';
  const b2cRoles = b2cRolesStr ? b2cRolesStr.split(',').map((r) => r.trim()) : [];
  const roles = entraRoles.length > 0 ? entraRoles : b2cRoles;

  // Parse permissions from claim (comma-separated) or derive from roles
  const permissionsStr = claims?.extension_Permissions ?? '';
  const permissions = permissionsStr
    ? permissionsStr.split(',').map((p) => p.trim())
    : roles.includes('System.Admin') || roles.includes('Organization.Admin') ? ['*'] : [];

  // Get email from various possible locations
  const email =
    claims?.emails?.[0] ??
    claims?.preferred_username ??
    account.username ??
    '';

  return {
    id: claims?.oid ?? account.localAccountId ?? '',
    displayName: claims?.name ?? account.name ?? 'Unknown User',
    email,
    firstName: claims?.given_name,
    lastName: claims?.family_name,
    tenantId: claims?.extension_TenantId,
    roles,
    permissions,
    idTokenClaims: claims as Record<string, unknown>,
  };
};

/**
 * Auth Provider component.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || null);

  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Update user from account when it changes.
   * Also force-refresh on mount to pick up role changes made since last login.
   */
  useEffect(() => {
    if (account) {
      const parsedUser = parseUserFromAccount(account);
      setUser(parsedUser);

      // Always force-refresh to pick up current roles from Entra ID.
      // MSAL's cached account.idTokenClaims may be stale — the response object has fresh claims.
      instance.acquireTokenSilent({
        ...idTokenRefreshRequest,
        account,
        forceRefresh: true,
      }).then((response) => {
        // Use idTokenClaims from the response directly (fresh), not from the cached account
        const freshClaims = response.idTokenClaims as TokenClaims | undefined;
        const responseAccount = response.account ?? account;
        const refreshedUser = parseUserFromAccount(responseAccount, freshClaims);
        if (refreshedUser) {
          setUser(refreshedUser);
        }
      }).catch(() => {
        // Silent refresh failed — user will get roles on next full login
      });
    } else {
      setUser(null);
    }
  }, [account, instance]);

  /**
   * Update loading state based on MSAL interaction status.
   */
  useEffect(() => {
    setIsLoading(inProgress !== InteractionStatus.None);
  }, [inProgress]);

  /**
   * Login function - initiates redirect login flow (primary).
   */
  const login = useCallback(async () => {
    try {
      setError(null);
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      console.error('Login failed:', error);
    }
  }, [instance]);

  /**
   * Login redirect flow (explicit alias).
   */
  const loginRedirect = useCallback(async () => {
    try {
      setError(null);
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      console.error('Login redirect failed:', error);
    }
  }, [instance]);

  /**
   * Logout function - clears session and redirects.
   */
  const logout = useCallback(async () => {
    try {
      setError(null);
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      console.error('Logout failed:', error);
    }
  }, [instance]);

  /**
   * Logout with redirect (explicit alias).
   */
  const logoutRedirect = useCallback(async () => {
    try {
      setError(null);
      setUser(null);
      setAccessToken(null);
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      console.error('Logout redirect failed:', error);
    }
  }, [instance]);

  /**
   * Get access token for API calls.
   * Uses silent token acquisition with fallback to redirect.
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) {
      return null;
    }

    try {
      const silentRequest: SilentRequest = {
        ...tokenRequest,
        account,
      };

      const response = await instance.acquireTokenSilent(silentRequest);
      setAccessToken(response.accessToken);
      return response.accessToken;
    } catch (err) {
      console.warn('Silent token acquisition failed:', err);

      // Don't attempt redirect if we're inside a hidden iframe (MSAL silent renewal)
      if (window !== window.parent) {
        console.warn('Skipping redirect — running inside iframe');
        return null;
      }

      // Only redirect if no other interaction is already in progress
      try {
        await instance.acquireTokenRedirect(tokenRequest);
        return null; // Will reload page
      } catch (redirectError) {
        const error =
          redirectError instanceof Error ? redirectError : new Error(String(redirectError));
        setError(error);
        console.error('Token redirect failed:', error);
        setUser(null);
        setAccessToken(null);
        return null;
      }
    }
  }, [instance, account]);

  /**
   * Clear any authentication errors.
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Force logout - clears all MSAL cache and redirects to login.
   * Use when the session is in a bad state.
   */
  const forceLogout = useCallback(() => {
    console.warn('Force logout - clearing all auth state');
    setUser(null);
    setAccessToken(null);

    // Clear MSAL cache from localStorage
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('msal.') || key.includes('login.windows.net') || key.includes('login.microsoftonline.com'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    window.location.href = '/login';
  }, []);

  // Create context value
  const contextValue: AuthContextValue = {
    isAuthenticated,
    isLoading,
    user,
    error,
    accessToken,
    login,
    loginRedirect,
    logout,
    logoutRedirect,
    getAccessToken,
    clearError,
    forceLogout,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context.
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Hook to check if user has a specific role.
 */
export const useHasRole = (role: string): boolean => {
  const { user } = useAuth();
  return user?.roles.includes(role) ?? false;
};

/**
 * Hook to check if user has a specific permission.
 */
export const useHasPermission = (permission: string): boolean => {
  const { user } = useAuth();
  if (!user) return false;

  if (user.permissions.includes('*')) return true;
  if (user.permissions.includes(permission)) return true;

  const [resource] = permission.split(':');
  return user.permissions.includes(`${resource}:*`);
};

export default AuthContext;
